import { ApiError, type Account, type Bootstrap, type NearbyAccount } from "../api";
import { shiftDay } from "../driverRoutes";
import { clockTime, readingTime } from "../format";
import { bootstrapKey, type Cached, read, type Scope, write } from "./db";

/** The longest wait for the network before a page shows the data on the phone. */
export const FETCH_WAIT_MS = 15_000;
export const DEFAULT_WINDOW_HOURS = 72;

class Timeout extends Error {}

/** An ApiError is an answer of the server; each other error means no answer. */
export const isNetworkError = (e: unknown) => !(e instanceof ApiError);

export function withWait<T>(work: Promise<T>, ms: number): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const late = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Timeout("no answer")), ms);
  });
  return Promise.race([work, late]).finally(() => clearTimeout(timer));
}

/** The time before a page gives a kept copy while it continues to wait for the network. */
export const KEPT_AFTER_MS = 1_500;

/** `at` is null for fresh data, else the time of the stored copy. */
export interface Found<T> {
  value: T;
  at: number | null;
  /** On a kept copy that a page gives before the answer: the network answer, which can come after the copy. */
  fresh?: Promise<Found<T>>;
}

const after = (ms: number) => new Promise<false>((resolve) => setTimeout(() => resolve(false), ms));

/** Get from the network and keep a copy. Give the copy on a network error, or after `keptAfter` ms with the answer in `fresh`. */
export async function cachedFetch<T>(
  key: string,
  load: () => Promise<T>,
  options: { saveAs?: (value: T) => string; wait?: number; keptAfter?: number | null } = {},
): Promise<Found<T>> {
  const kept = read<T>(key);
  const answer = withWait(load(), options.wait ?? FETCH_WAIT_MS).then(async (value): Promise<Found<T>> => {
    await write(options.saveAs?.(value) ?? key, value);
    return { value, at: null };
  });
  const fallback = async (e: unknown): Promise<Found<T>> => {
    if (!isNetworkError(e)) throw e;
    const copy = await kept;
    if (!copy) throw e;
    return { value: copy.value, at: copy.at };
  };
  const settled = answer.then(
    () => true,
    () => true,
  );
  const keptAfter = options.keptAfter === undefined ? KEPT_AFTER_MS : options.keptAfter;
  if (keptAfter === null || (await Promise.race([settled, after(keptAfter)]))) return answer.catch(fallback);
  const copy = await kept;
  if (!copy) return answer.catch(fallback);
  return { value: copy.value, at: copy.at, fresh: answer };
}

/** The late answer, or null when the page saved after this call. */
export function unlessSaved<T>(fresh: Promise<T>, saves: { current: number }): Promise<T | null> {
  const mark = saves.current;
  return fresh.then((value) => (saves.current === mark ? value : null));
}

export const loadBootstrap = (slug: string, load: () => Promise<Bootstrap>) => cachedFetch(bootstrapKey(slug), load);

/** The next day from Monday to Friday. */
export function nextWorkday(day: string): string {
  let next = shiftDay(day, 1);
  while ([0, 6].includes(new Date(`${next}T12:00:00Z`).getUTCDay())) next = shiftDay(next, 1);
  return next;
}

export interface CompactAccount {
  id: string;
  number: string;
  name: string;
  address?: string;
  pin?: { lat: number; lng: number } | null;
  capacity_gal?: number;
}

export const compactAccount = (account: Account): CompactAccount => {
  const pin = account.tank ?? account.door;
  return {
    id: account.id,
    number: account.number,
    name: account.name,
    ...(account.address ? { address: account.address } : {}),
    pin: pin ? { lat: pin.lat, lng: pin.lng } : null,
    capacity_gal: account.capacity_gal,
  };
};

const EARTH_M = 6_371_000;

export function distanceM(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const rad = Math.PI / 180;
  const dLat = (b.lat - a.lat) * rad;
  const dLng = (b.lng - a.lng) * rad;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** The nearest accounts with a pin, as /api/visits/nearby gives them. */
export function nearbyLocal(accounts: CompactAccount[], at: { lat: number; lng: number }, limit = 10): NearbyAccount[] {
  return accounts
    .flatMap((account) =>
      account.pin
        ? [{ id: account.id, number: account.number, name: account.name, address: account.address ?? "", distance_m: distanceM(at, account.pin) }]
        : [],
    )
    .sort((a, b) => a.distance_m - b.distance_m)
    .slice(0, limit);
}

/** The keys of the day records that Today keeps. */
export const dayKeys = {
  route: (day: string) => `route:${day}`,
  loads: (day: string) => `loads:${day}`,
  ready: (day: string) => `ready:${day}`,
  defaults: "visit-defaults",
  labels: "visit-labels",
  accounts: "accounts",
  can: "can",
  online: "online",
};

export interface Ready {
  stops: number;
}

/** Each part that Today and Visit need offline, written for `today`. */
export async function isDayReady(scope: Scope, today: string): Promise<Cached<Ready> | null> {
  return scope.read<Ready>(dayKeys.ready(today));
}

/** True when the last online token is older than the offline window. */
export function signInDue(lastOnline: number | null, windowHours: number, now: number = Date.now()): boolean {
  return lastOnline !== null && now - lastOnline > windowHours * 3_600_000;
}

/** The offline window in hours; the organization setting replaces the default in C1. */
export const offlineWindowHours = (): number => DEFAULT_WINDOW_HOURS;

/** A kept day younger than this is fresh enough; Today then does not fetch the day again. */
export const DAY_FRESH_MS = 30 * 60_000;

/** True when Today must fetch the day again: a retry, no kept day, an old one, or a route start or finish sent after it. */
export function dayRefreshDue(keptAt: number | null, retry: boolean, now: number = Date.now(), changedAt: number | null = null): boolean {
  return retry || keptAt === null || now - keptAt >= DAY_FRESH_MS || (changedAt !== null && changedAt > keptAt);
}

function dataTime(at: number, timeZone: string, now: number): string {
  const day = (t: number) => new Intl.DateTimeFormat("en-CA", { timeZone, dateStyle: "short" }).format(t);
  return day(at) === day(now) ? clockTime(new Date(at), timeZone) : readingTime(new Date(at).toISOString(), timeZone);
}

/** "Offline — data from 7:12 AM EDT", with the day when the copy is older than today. */
export function offlineText(at: number, timeZone: string, now: number = Date.now()): string {
  return `Offline — data from ${dataTime(at, timeZone, now)}`;
}

/** The note on a kept copy while the network can send an answer. */
export function checkingText(at: number, timeZone: string, now: number = Date.now()): string {
  return `Checking for new data… Data from ${dataTime(at, timeZone, now)}`;
}
